import { MultiSelectMenu, Table as TableCanoe } from 'canoe';
import {
  compose,
  connectWithHammock,
  ENDPOINTS,
  fetchResource,
  useActions,
  useCreatePayload,
} from 'hammock';
import React, { useMemo, useState } from 'react';
import { useMutation } from 'react-query';

import {
  FormResponseAction,
  FormResponseCount,
  FormType,
  MostRecentFormSubmission,
} from '@candu-ai/forms/types';
import { useNavigation } from 'commons/hooks';
import TimeAgo from 'dashboard/components/TimeAgo';
import { RESPONSES_PATH } from 'dashboard/pages/Forms/constants';

export interface FormsListProps {
  forms: FormType[];
  formResponseCount: FormResponseCount[];
  formMostRecentSubmission: MostRecentFormSubmission[];
}

type FormWithResponseCountAndDate = FormType & {
  responseCount: number;
  mostRecentSubmission?: string;
};

export const FormsListImpl: React.FC<FormsListProps> = ({
  forms,
  formResponseCount,
  formMostRecentSubmission,
}: FormsListProps) => {
  const { action: deleteAction } = useActions(ENDPOINTS.formResponses);
  const { action: actionPayload } = useCreatePayload(ENDPOINTS.formResponses);

  const navigate = useNavigation();
  const [selectedCells, setSelectedCells] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');

  const downloadCSVPayload = actionPayload({
    name: FormResponseAction.DownloadCSV,
    id: selectedCells[0],
  });

  const { mutateAsync: downloadCsv, isLoading: isDownloadingCsv } = useMutation<{
    s3Url: string;
  }>({
    mutationFn: () => fetchResource(downloadCSVPayload),
  });

  const onDownloadCSV = () => {
    downloadCsv().then((response) => window.open(response.s3Url));
  };

  const formsWithResponse = forms
    .map((form) => {
      const responseCount =
        formResponseCount.find(({ id }) => id === form.formId)?.responseCount || 0;

      const mostRecentSubmission = formMostRecentSubmission.find(
        ({ formId }) => formId === form.formId,
      )?.mostRecentSubmission;
      return { ...form, responseCount, mostRecentSubmission };
    })
    .sort((a, b) => {
      if (
        !b.mostRecentSubmission ||
        new Date(b.mostRecentSubmission).getTime() <
          new Date(a.mostRecentSubmission ?? '').getTime()
      ) {
        return -1;
      }

      return 1;
    });

  const filteredForms: FormWithResponseCountAndDate[] = useMemo(
    () =>
      formsWithResponse.filter((form) =>
        form.name.toLowerCase().includes(searchValue.toLowerCase()),
      ),
    [formsWithResponse, searchValue],
  );

  const onNavigateToFormResponses = (id: string) => {
    navigate(`${RESPONSES_PATH}/${id}`);
  };

  const onBulkDelete = async () => {
    await deleteAction({
      name: FormResponseAction.DeleteResponses,
      body: {
        formIds: selectedCells,
      },
      onSuccessNotification: {
        message: `Responses successfully deleted`,
      },
      invalidates: {
        [ENDPOINTS.forms]: true,
        [ENDPOINTS.formResponseCount]: true, // reset the response counter
        [ENDPOINTS.formResponses]: true,
      },
    });
    setSelectedCells([]);
  };

  if (forms.length === 0) {
    return <div id="candu-forms-emptystate"></div>;
  }

  return (
    <>
      <div>
        <TableCanoe<FormWithResponseCountAndDate>
          maxVisibleRows={9}
          rows={filteredForms}
          onRow={(record) => ({
            onClick: () => onNavigateToFormResponses(record.formId),
          })}
          rowSelection={{
            type: 'checkbox',
            getCheckboxProps: (record) => ({
              name: record.formId,
            }),
            onChange: (selectedRowKeys) => {
              setSelectedCells(selectedRowKeys as string[]);
            },
          }}
          onFilterChange={(column, value) => {
            setSearchValue(value ?? '');
          }}
          searchingColumn="name"
          searchingValue={searchValue}
          selected={selectedCells}
          columns={[
            {
              key: 'name',
              dataIndex: 'name',
              title: 'Form name',
              isSearchable: true,
            },
            {
              key: 'responseCount',
              dataIndex: 'responseCount',
              title: 'Response count',
            },
            {
              key: 'lastResponse',
              dataIndex: 'responseCount',
              title: 'Last response',
              render: (text, record) =>
                record.mostRecentSubmission ? (
                  <TimeAgo date={record.mostRecentSubmission} />
                ) : (
                  'never'
                ),
            },
          ]}
        />
      </div>

      <MultiSelectMenu
        isOpen={selectedCells.length > 0}
        label={`${selectedCells.length} form${selectedCells.length > 1 ? 's' : ''} selected`}
        onClose={() => setSelectedCells([])}
        dangerAction={{
          children: `Delete form responses`,
          'data-cy': 'delete-form-responses',
          onClick: onBulkDelete,
        }}
        successAction={
          selectedCells.length === 1
            ? {
                onClick: onDownloadCSV,
                loading: isDownloadingCsv,
                children: 'Export to CSV',
                'data-cy': 'export-form-responses',
              }
            : undefined
        }
      />
    </>
  );
};

const withCollections = connectWithHammock({
  endpoint: ENDPOINTS.forms,
  resourceName: 'forms',
});

const withCounts = connectWithHammock({
  endpoint: ENDPOINTS.formResponseCount,
  resourceName: 'formResponseCount',
});

const withFormLastResponses = connectWithHammock(() => ({
  endpoint: ENDPOINTS.formMostRecentSubmission,
  resourceName: 'formMostRecentSubmission',
}));

export const FormsList = compose(withCollections, withCounts, withFormLastResponses)(FormsListImpl);
